import { FC } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { CreateContactAttemptDto } from "@myapp/contract";
import { FieldWrapper } from "../../../../shared/ui";
import { FormInput } from "../../../../shared/ui/controls/FormInput";
import { useHoldUpdateWhileDirty } from "../../../../shared/lib/app-version";
import {
  AttemptFormValues,
  attemptFormSchema,
  defaultAttemptValues,
  toCreateDto,
} from "../../model/attemptForm";

interface Props {
  /** The number the new attempt will get. */
  position: number;
  isSaving: boolean;
  /** The server's reason, when the add was refused (limit reached, status changed meanwhile). */
  error: string | null;
  /** Resolves true when the attempt was stored — the form then closes itself. */
  onSubmit: (dto: CreateContactAttemptDto) => Promise<boolean>;
  onCancel: () => void;
}

/**
 * Opens in place of the dashed „Noch offen" row rather than in a modal: the list grows where the
 * next number already is. Date and time are prefilled with now, so the common case is one note and
 * one press of Enter; the office can backdate a call it forgot to log.
 */
export const AttemptForm: FC<Props> = ({ position, isSaving, error, onSubmit, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<AttemptFormValues>({
    resolver: zodResolver(attemptFormSchema),
    defaultValues: defaultAttemptValues(),
  });

  // A half-filled attempt must not be thrown away by an app update reloading the page.
  useHoldUpdateWhileDirty(isDirty);

  const submit = handleSubmit(async (values) => {
    const stored = await onSubmit(toCreateDto(values));
    if (stored) onCancel();
  });

  return (
    <li className="grid grid-cols-[28px_minmax(0,1fr)] gap-3 border-t border-slate-200 pt-3">
      <span
        aria-hidden="true"
        className="mt-2 grid h-7 w-7 place-items-center rounded-full bg-[#D64541] text-[13px] font-bold text-white"
      >
        {position}
      </span>
      <form onSubmit={submit} noValidate className="flex min-w-0 flex-col gap-2.5" aria-label={`${position}. Kontaktversuch erfassen`}>
        <FieldWrapper label="Was wurde versucht?" error={errors.note?.message}>
          <FormInput<AttemptFormValues>
            name="note"
            control={control}
            placeholder="z. B. Anruf, Mailbox"
            autoComplete="off"
            autoFocus
            disabled={isSaving}
          />
        </FieldWrapper>
        <div className="grid grid-cols-2 gap-2.5">
          <FieldWrapper label="Datum" error={errors.date?.message}>
            <FormInput<AttemptFormValues> name="date" control={control} type="date" disabled={isSaving} />
          </FieldWrapper>
          <FieldWrapper label="Uhrzeit" error={errors.time?.message}>
            <FormInput<AttemptFormValues> name="time" control={control} type="time" disabled={isSaving} />
          </FieldWrapper>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="h-[38px] rounded-full px-4 text-[15px] font-bold text-slate-700 transition-colors duration-150 hover:bg-slate-100 disabled:opacity-60"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={isSaving}
            aria-busy={isSaving || undefined}
            className="h-[38px] rounded-full bg-[#124093] px-4 text-[15px] font-bold text-white transition-[filter] duration-150 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? "Wird gespeichert…" : "Versuch erfassen"}
          </button>
        </div>
      </form>
    </li>
  );
};
